/**
 * The one breakpoint (INVENTORY-DESKTOP §10.3).
 *
 * `App.tsx` renders `wide ? <DesktopApp/> : <MobileApp/>`; this hook is the
 * only thing that decides which. 760px is the prototype's own switch verbatim
 * (`const isMobile = S.w < 760`, `design/Games Tonight.dc.html:2125`) and is
 * also the GOTY two-column floor: `flex:2 min-width:340` + `gap:22` +
 * `width:320` rail + `2×26` gutters = 760px exactly.
 *
 * WHY NOT `resize` → `setState`: the prototype re-rendered the whole app on
 * every resize event (~60/s while a window edge is dragged) purely to recompute
 * one boolean. `matchMedia` fires exactly twice — once crossing each way — and
 * `useSyncExternalStore` gives React a tear-free read with no effect pass.
 *
 * SSR / jsdom without matchMedia: the snapshot is `false` (mobile-first), which
 * matches the server snapshot, so there is no hydration mismatch.
 *
 * FULLSCREEN HOLDS THE CROSSING, and that is the whole reason this file is more
 * than twenty lines. Crossing 760px unmounts one tree and mounts the other
 * (App.tsx) — which is fine for a dragged window edge and fatal for a phone.
 * Every current phone is 360–430px wide in portrait and 800–930px in landscape,
 * so ROTATING ONE CROSSES THIS QUERY. A user who plays a trailer, goes
 * fullscreen and turns the phone sideways to make it bigger was having the
 * `<video>`/`<iframe>` torn out of the document mid-playback: the browser drops
 * fullscreen when its fullscreen element leaves the DOM, so the video appeared
 * to close itself at the exact moment it should have gotten larger.
 *
 * So while ANY fullscreen presentation is up, `read` keeps returning the value
 * it last returned and the crossing is deferred; leaving fullscreen wakes every
 * subscriber and the layout catches up then. Holding is safe by inspection —
 * the held tree is behind a fullscreen surface, so nobody can see it — and it
 * is a hold, never a veto: the two states cannot drift because the query is
 * re-read the moment fullscreen ends.
 *
 * KNOWN GAP: an iPhone cannot report a cross-origin iframe's OWN fullscreen.
 * The Fullscreen API is iPad-and-up, so YouTube's embed goes fullscreen by
 * calling `webkitEnterFullscreen()` on its video inside youtube-nocookie.com's
 * document, which raises no event we can see. Steam trailers (a same-document
 * `<video>`, covered below) and every non-iPhone browser (real Fullscreen API)
 * are handled. Closing that one needs a device-aware query rather than a hold.
 *
 * A HANDSET NEVER CROSSES AT ALL (2026-09-17), and that is the device-aware
 * half. The hold only covers the window in which a fullscreen element exists,
 * and Chrome for Android moves the viewport on BOTH sides of that window. All
 * three were reproduced on an emulator (Chrome 145, a real Steam trailer):
 *
 *   1. Leaving fullscreen. Chrome force-rotates a landscape video to landscape
 *      even while the phone is held upright, and only releases that lock AFTER
 *      `fullscreenchange`. The re-read "the moment fullscreen ends" therefore
 *      saw 914px, mounted the desktop tree, destroyed the `<video>`, and ~1s
 *      later the screen turned back and the mobile tree mounted again.
 *   2. Leaving fullscreen with the phone genuinely sideways — same teardown,
 *      minus the flip back.
 *   3. Turning the phone with a trailer playing INLINE. No fullscreen element
 *      yet, so no hold; the tree swap killed the video before Chrome's own
 *      rotate-to-fullscreen could act.
 *
 * A phone is ~360–480 CSS px on its short side and cannot usefully host the
 * desktop tree in ~330px of landscape height anyway, so `handset()` pins it to
 * the mobile tree in either orientation. The hold stays for everything that
 * can still legitimately cross — foldables, small tablets, a dragged window.
 */

import { useCallback, useSyncExternalStore } from 'react';

/** INVENTORY-DESKTOP §10.3 — the desktop tree owns everything at or above this. */
export const DESKTOP_QUERY = '(min-width: 760px)';

/* --- is something on screen fullscreen right now? ------------------------ */

/** Safari kept its prefix on the document-level element long after unprefixing. */
type FsDocument = Document & { webkitFullscreenElement?: Element | null };

/**
 * Elements iOS is showing in its system player. iOS has no document-level
 * fullscreen element for a `<video>` that entered fullscreen from its own
 * controls, so this set IS the state, kept by the two events below.
 */
const systemPlayer = new Set<EventTarget>();

/** Every live subscriber, so leaving fullscreen can wake all of them at once. */
const wakers = new Set<() => void>();

/** Non-null while the document listeners are attached; also detaches them. */
let unwatch: (() => void) | null = null;

function fullscreen(): boolean {
  if (systemPlayer.size > 0) return true;
  if (typeof document === 'undefined') return false;
  const d = document as FsDocument;
  return !!(d.fullscreenElement ?? d.webkitFullscreenElement);
}

const wake = (): void => {
  // Copied: a subscriber may unsubscribe from inside its own callback.
  for (const fn of [...wakers]) fn();
};

const onEnterSystemPlayer = (e: Event): void => {
  if (e.target) systemPlayer.add(e.target);
};

const onLeaveSystemPlayer = (e: Event): void => {
  if (e.target) systemPlayer.delete(e.target);
  wake();
};

function watchFullscreen(): void {
  if (unwatch || typeof document === 'undefined') return;
  const d = document;

  // Standard, plus Safari's prefix. Both fire on entering AND leaving; `read`
  // ignores the entering ones by construction, so one handler covers both.
  d.addEventListener('fullscreenchange', wake);
  d.addEventListener('webkitfullscreenchange', wake);

  // iOS presents a `<video>` in the system player instead: no document
  // fullscreen element and no *change* event, only these two — dispatched at
  // the element and NOT bubbling, which is why these listeners are CAPTURING.
  // A non-bubbling event still runs the capture phase from the document down.
  d.addEventListener('webkitbeginfullscreen', onEnterSystemPlayer, true);
  d.addEventListener('webkitendfullscreen', onLeaveSystemPlayer, true);

  unwatch = (): void => {
    d.removeEventListener('fullscreenchange', wake);
    d.removeEventListener('webkitfullscreenchange', wake);
    d.removeEventListener('webkitbeginfullscreen', onEnterSystemPlayer, true);
    d.removeEventListener('webkitendfullscreen', onLeaveSystemPlayer, true);
    systemPlayer.clear();
    unwatch = null;
  };
}

/* --- the store -------------------------------------------------------- */

/**
 * One `MediaQueryList` per query string, shared by every hook instance. The
 * list object must be stable or `useSyncExternalStore` would resubscribe on
 * every render.
 */
const lists = new Map<string, MediaQueryList | null>();

/** Last value handed to React per query — what a fullscreen hold holds. */
const held = new Map<string, boolean>();

function listFor(query: string): MediaQueryList | null {
  if (lists.has(query)) return lists.get(query) ?? null;
  const mm =
    typeof window === 'undefined' || typeof window.matchMedia !== 'function' ? null : window.matchMedia(query);
  lists.set(query, mm);
  return mm;
}

/**
 * `getSnapshot`. Reads through to `matchMedia` unless a fullscreen presentation
 * is up and we have a previous answer to stand on — see the file header. Doing
 * the hold HERE rather than in the listener is what keeps it from going stale:
 * there is no cached decision to invalidate, only a live read that is skipped.
 */
export function readBreakpoint(query: string = DESKTOP_QUERY): boolean {
  // Before the hold, not after: a handset's answer never depends on what was
  // held, and an early return keeps `held` from ever caching `true` for one.
  if (query === DESKTOP_QUERY && handset()) return false;
  const prev = held.get(query);
  if (prev !== undefined && fullscreen()) return prev;
  const now = listFor(query)?.matches ?? false;
  held.set(query, now);
  return now;
}

/**
 * Short side of the SCREEN (not the window) below which a device is a phone.
 * Phones top out near 480 CSS px; foldables' inner displays start near 690 and
 * tablets near 744, so 600 splits them with room on both sides. The smallest
 * desktop panels still in use (1280×720) sit well above it.
 */
export const HANDSET_MAX_SHORT_SIDE = 600;

/**
 * Is this a phone? Asked of `screen`, which is the device and does not change
 * when the window or the orientation does — Android swaps `width`/`height` on
 * rotation and iOS does not, which is why it is the MIN of the two. `0` is
 * jsdom, which has a `screen` with no dimensions.
 */
function handset(): boolean {
  if (typeof screen === 'undefined') return false;
  const short = Math.min(screen.width, screen.height);
  return short > 0 && short < HANDSET_MAX_SHORT_SIDE;
}

/** `subscribe`. Wakes on a query crossing OR on leaving fullscreen. */
export function subscribeBreakpoint(query: string, onChange: () => void): () => void {
  wakers.add(onChange);
  if (wakers.size === 1) watchFullscreen();

  const mm = listFor(query);
  let offQuery = (): void => {};
  if (mm) {
    // Safari < 14 has no `addEventListener` on MediaQueryList; the deprecated
    // `addListener` is the documented fallback and is still shipped.
    if (typeof mm.addEventListener === 'function') {
      mm.addEventListener('change', onChange);
      offQuery = () => mm.removeEventListener('change', onChange);
    } else {
      mm.addListener(onChange);
      offQuery = () => mm.removeListener(onChange);
    }
  }

  return () => {
    offQuery();
    wakers.delete(onChange);
    if (wakers.size === 0) unwatch?.();
  };
}

/**
 * `true` while the query matches. Re-renders only on an actual crossing.
 *
 *   const wide = useBreakpoint();                       // (min-width: 760px)
 *   const huge = useBreakpoint('(min-width: 1440px)');  // any other query
 */
export function useBreakpoint(query: string = DESKTOP_QUERY): boolean {
  const subscribe = useCallback((onChange: () => void) => subscribeBreakpoint(query, onChange), [query]);
  const getSnapshot = useCallback(() => readBreakpoint(query), [query]);
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Test seam — drops all cached state. Assumes nothing is currently subscribed. */
export function resetBreakpointCache(): void {
  unwatch?.();
  wakers.clear();
  lists.clear();
  held.clear();
}
